import { useCallback, useEffect, useState } from 'react';
import { getNotificationsSummary, markNotificationsSeen } from '../services/notifications';
import { isMissingEndpoint, normalizeNotificationSummary } from '../utils/notificationSummary';
import { APP_EVENTS, useAppEvent } from '../utils/appEvents';

/**
 * Состояние колокольчика.
 *
 * `unsupported` — бэкенд ещё не отдаёт /notifications/*: колокольчика на экране
 * быть не должно. `error` — эндпоинт есть, но ответил плохо: показываем
 * «Повторить». Эти два случая нельзя смешивать (Bell Addendum, раздел 12).
 */
export const useNotificationSummary = () => {
  const [state, setState] = useState('loading'); // loading | ready | error | unsupported
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState('');

  const refresh = useCallback(() => {
    // Если данные уже загружены, обновляем тихо; иначе показываем загрузку
    setState((current) => {
      if (current === 'ready') return current;
      if (current === 'unsupported') return current;
      return 'loading';
    });

    return getNotificationsSummary()
      .then((data) => {
        const summary = normalizeNotificationSummary(data);
        setUnreadCount(summary.unreadCount);
        setItems(summary.items);
        setState('ready');
      })
      .catch((error) => {
        // Проверяем, является ли ошибка отсутствием эндпоинта (404/501)
        // и устанавливаем соответствующее состояние
        if (isMissingEndpoint(error)) {
          setState('unsupported');
        } else {
          setState('error');
        }
      });
  }, []);

  useEffect(() => {
    refresh().catch(() => {
      // Error already handled in refresh's catch block
    });
  }, [refresh]);

  // ТЗ 11: backend двигает seen-cursor при успешном GET карточки события —
  // колокольчик должен подхватить это немедленно, а не ждать смены вкладки.
  useAppEvent(APP_EVENTS.NOTIFICATIONS_REFRESH, refresh);

  // Возврат приложения из background — единственный триггер обновления:
  // постоянный polling ТЗ не требует, а на мобильном он дорог.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const markAllSeen = useCallback(() => {
    setMarking(true);
    setMarkError('');
    return markNotificationsSeen()
      .then(() => {
        setUnreadCount(0);
        setItems((prev) => prev.map((item) => ({ ...item, unread: false })));
      })
      .catch(() => setMarkError('Не удалось отметить прочитанным'))
      .finally(() => setMarking(false));
  }, []);

  return { state, unreadCount, items, marking, markError, refresh, markAllSeen };
};

export default useNotificationSummary;
